import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import * as Sentry from "@sentry/react";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ReferralConfirm from "./pages/ReferralConfirm";
import InviteLanding from "./pages/InviteLanding";
import AuthCallback from "./pages/AuthCallback";
import ConsentRegolamento from "./pages/ConsentRegolamento";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetupPassword from "./pages/SetupPassword";
import Privacy from "./pages/Privacy";
import CookiePolicy from "./pages/CookiePolicy";
import NotFound from "./pages/NotFound";
import CookieBanner from "./components/CookieBanner";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F3]">
        <div className="text-stone-500 text-sm tracking-wider uppercase">Caricamento…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_consent) return <Navigate to="/consenso" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_consent) return <Navigate to="/consenso" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

function AppRoutes() {
  const location = useLocation();
  // Detect Emergent OAuth callback fragment synchronously (before any route renders).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/registrati" element={<Register />} />
      <Route path="/referral/conferma/:token" element={<ReferralConfirm />} />
      <Route path="/invito/:code" element={<InviteLanding />} />
      <Route path="/r/:code" element={<InviteLanding />} />
      <Route path="/consenso" element={<ConsentRegolamento />} />
      <Route path="/password-dimenticata" element={<ForgotPassword />} />
      <Route path="/password-reset/:token" element={<ResetPassword />} />
      <Route path="/imposta-password/:token" element={<SetupPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/me" element={<RoleRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-[#F7F6F3] px-6">
          <div className="max-w-md text-center">
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Si è verificato un errore</h2>
            <p className="text-stone-600 mb-6">
              Il nostro team è stato avvisato automaticamente. Ti chiediamo scusa per il disagio.
            </p>
            <button
              onClick={resetError}
              data-testid="sentry-reset-error-btn"
              className="px-6 py-3 bg-stone-900 text-white rounded-full hover:bg-stone-800 transition"
            >
              Ricarica la pagina
            </button>
          </div>
        </div>
      )}
    >
      <div className="App grain">
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <CookieBanner />
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </div>
    </Sentry.ErrorBoundary>
  );
}

export default App;
